import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Table,
  Button,
  Spinner,
  Collapse,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";

import {
  masterThirdPartyPricingList,
  masterThirdPartyPricingCreate,
  masterThirdPartyPricingUpdate,
  masterThirdPartyPricingDelete,
} from "../../../../store/actions";
import AppModal from "../../../../Components/Common/AppModal";
import SettingsPanel from "../../components/SettingsPanel";

const emptyBracket = {
  VolumeLowerBound: "",
  VolumeUpperBound: "",
  PricePerUnitQuantity: "",
  PriceAmount: "",
  MarkupPercentage: "",
  SortOrder: "",
};

const initialFormState = {
  Provider: "",
  Category: "",
  Name: "",
  SkuCode: "",
  CurrencyCode: "USD",
  Url: "",
  FlagUnlimitedFree: "",
  Notes: "",
  PriceEffectiveStartDateTime: "",
  PriceEffectiveEndDateTime: "",
};

const MasterThirdPartyPricing = () => {
  const dispatch = useDispatch();

  // 1. Fetch State from Redux
  const {
    masterThirdPartyPricingList: pricingData,
    masterThirdPartyPricingListLoading: isLoading,
  } = useSelector((state) => state.masterThirdPartyPricingReducer);

  // Safely destructure the array from the API response
  const pricingList = pricingData?.masterthirdpartypricing_list || [];

  // 2. Local State
  const [formData, setFormData] = useState(initialFormState);
  const [brackets, setBrackets] = useState([{ ...emptyBracket }]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  // ids of rows whose bracket accordion is expanded
  const [expandedIds, setExpandedIds] = useState([]);

  const toggleExpand = (id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // 3. Fetch Data on Mount
  useEffect(() => {
    dispatch(masterThirdPartyPricingList());
  }, [dispatch]);

  // 4. Handlers
  const openModal = (item = null) => {
    if (item && item.Id) {
      setIsEditing(true);
      setEditId(item.Id);
      setFormData({
        Provider: item.Provider || "",
        Category: item.Category || "",
        Name: item.Name || "",
        SkuCode: item.SkuCode || "",
        CurrencyCode: item.CurrencyCode || "USD",
        Url: item.Url || "",
        FlagUnlimitedFree: item.FlagUnlimitedFree || "",
        Notes: item.Notes || "",
        PriceEffectiveStartDateTime: item.PriceEffectiveStartDateTime || "",
        PriceEffectiveEndDateTime: item.PriceEffectiveEndDateTime || "",
      });
      setBrackets(
        Array.isArray(item.brackets) && item.brackets.length
          ? item.brackets.map((b) => ({
              VolumeLowerBound: b.VolumeLowerBound ?? "",
              VolumeUpperBound: b.VolumeUpperBound ?? "",
              PricePerUnitQuantity: b.PricePerUnitQuantity ?? "",
              PriceAmount: b.PriceAmount ?? "",
              MarkupPercentage: b.MarkupPercentage ?? "",
              SortOrder: b.SortOrder ?? "",
            }))
          : [{ ...emptyBracket }]
      );
    } else {
      setIsEditing(false);
      setEditId(null);
      setFormData(initialFormState);
      setBrackets([{ ...emptyBracket }]);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData(initialFormState);
    setBrackets([{ ...emptyBracket }]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBracketChange = (index, e) => {
    const { name, value } = e.target;
    setBrackets((prev) =>
      prev.map((b, i) => (i === index ? { ...b, [name]: value } : b))
    );
  };

  const addBracket = () => {
    setBrackets((prev) => [...prev, { ...emptyBracket }]);
  };

  const removeBracket = (index) => {
    setBrackets((prev) => prev.filter((_, i) => i !== index));
  };

  const buildBracketsPayload = () =>
    brackets
      // Only keep brackets that have at least a lower bound or a price set.
      .filter(
        (b) =>
          b.VolumeLowerBound !== "" ||
          b.PriceAmount !== "" ||
          b.PricePerUnitQuantity !== ""
      )
      .map((b, i) => {
        const row = {};
        if (b.VolumeLowerBound !== "") row.VolumeLowerBound = b.VolumeLowerBound;
        if (b.VolumeUpperBound !== "") row.VolumeUpperBound = b.VolumeUpperBound;
        if (b.PricePerUnitQuantity !== "")
          row.PricePerUnitQuantity = b.PricePerUnitQuantity;
        if (b.PriceAmount !== "") row.PriceAmount = b.PriceAmount;
        if (b.MarkupPercentage !== "")
          row.MarkupPercentage = b.MarkupPercentage;
        row.SortOrder = b.SortOrder !== "" ? b.SortOrder : i;
        return row;
      });

  const handleSubmit = () => {
    const service = { ...formData, brackets: buildBracketsPayload() };

    if (isEditing) {
      dispatch(masterThirdPartyPricingUpdate([{ Id: editId, ...service }]));
    } else {
      dispatch(masterThirdPartyPricingCreate([{ ...service }]));
    }

    closeModal();
    setTimeout(() => dispatch(masterThirdPartyPricingList()), 500);
  };

  const handleDeleteClick = (id) => {
    if (window.confirm("Are you sure you want to delete this pricing service?")) {
      dispatch(masterThirdPartyPricingDelete([id]));
      setTimeout(() => dispatch(masterThirdPartyPricingList()), 500);
    }
  };

  return (
    <React.Fragment>
      <SettingsPanel
        title="Third-Party Pricing"
        description="Master catalog of third-party metered service costs (e.g. Google Maps Platform SKUs) with tiered volume brackets."
      >
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h4 className="card-title mb-0">Master Third-Party Pricing</h4>
                  <div className="d-flex gap-2">
                    <Button
                      color="light"
                      size="sm"
                      className="border"
                      onClick={() => dispatch(masterThirdPartyPricingList())}
                      disabled={isLoading}
                    >
                      <i className="ri-refresh-line align-middle me-1"></i>
                      Refresh
                    </Button>
                    <Button color="primary" size="sm" onClick={() => openModal()}>
                      <i className="ri-add-line align-middle me-1"></i>
                      Add Pricing
                    </Button>
                  </div>
                </div>

                <div className="table-responsive">
                  <Table className="table table-striped table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "40px" }}></th>
                        <th>Provider</th>
                        <th>Category</th>
                        <th>Name</th>
                        <th>SKU</th>
                        <th>Currency</th>
                        <th className="text-center">Brackets</th>
                        <th>URL</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td colSpan="9" className="text-center text-muted py-4">
                            <Spinner size="sm" className="me-2" />
                            Loading pricing...
                          </td>
                        </tr>
                      ) : pricingList.length > 0 ? (
                        pricingList.map((item, idx) => {
                          const brackets = Array.isArray(item.brackets)
                            ? item.brackets
                            : [];
                          const bracketCount = brackets.length;
                          const rowId = item.Id ?? idx;
                          const isExpanded = expandedIds.includes(rowId);
                          return (
                            <React.Fragment key={rowId}>
                              <tr
                                className={isExpanded ? "table-active" : ""}
                                style={{ cursor: bracketCount ? "pointer" : "default" }}
                                onClick={() =>
                                  bracketCount && toggleExpand(rowId)
                                }
                              >
                                <td className="text-center">
                                  {bracketCount ? (
                                    <i
                                      className="ri-arrow-right-s-line align-middle fs-5 text-muted"
                                      style={{
                                        display: "inline-block",
                                        transition: "transform 0.15s",
                                        transform: isExpanded
                                          ? "rotate(90deg)"
                                          : "none",
                                      }}
                                    ></i>
                                  ) : null}
                                </td>
                                <td className="fw-medium">{item.Provider}</td>
                                <td>
                                  {item.Category ? (
                                    <span className="badge bg-info-subtle text-info text-uppercase">
                                      {item.Category}
                                    </span>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                                <td>{item.Name}</td>
                                <td>
                                  {item.SkuCode ? (
                                    <code>{item.SkuCode}</code>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                                <td>{item.CurrencyCode}</td>
                                <td className="text-center">
                                  <span className="badge bg-primary-subtle text-primary">
                                    {bracketCount}
                                  </span>
                                </td>
                                <td>
                                  {item.Url ? (
                                    <a
                                      href={item.Url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-primary text-decoration-none"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <i className="ri-external-link-line align-middle me-1"></i>
                                      Open
                                    </a>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                                <td className="text-end">
                                  <Button
                                    color="soft-primary"
                                    size="sm"
                                    className="me-1"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openModal(item);
                                    }}
                                    title="Edit"
                                  >
                                    <i className="ri-pencil-line align-middle"></i>
                                  </Button>
                                  <Button
                                    color="soft-danger"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteClick(item.Id);
                                    }}
                                    title="Delete"
                                  >
                                    <i className="ri-delete-bin-line align-middle"></i>
                                  </Button>
                                </td>
                              </tr>

                              {/* ACCORDION: bracket rows */}
                              <tr className="bracket-accordion-row">
                                <td colSpan="9" className="p-0 border-0">
                                  <Collapse isOpen={isExpanded}>
                                    <div className="bg-light-subtle px-3 py-2">
                                      <h6 className="mb-2 text-muted">
                                        Volume Brackets
                                      </h6>
                                      <Table
                                        size="sm"
                                        className="table align-middle mb-0"
                                      >
                                        <thead>
                                          <tr>
                                            <th>Vol. Lower</th>
                                            <th>Vol. Upper</th>
                                            <th>Per Qty</th>
                                            <th>Price Amt</th>
                                            <th>Per Unit</th>
                                            <th>Markup %</th>
                                            <th>Est. Retail</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {brackets.map((b, bIdx) => (
                                            <tr key={b.Id || bIdx}>
                                              <td>{b.VolumeLowerBound}</td>
                                              <td>
                                                {b.VolumeUpperBound ?? (
                                                  <span className="text-muted">
                                                    ∞
                                                  </span>
                                                )}
                                              </td>
                                              <td>{b.PricePerUnitQuantity}</td>
                                              <td>
                                                {item.CurrencyCode}{" "}
                                                {b.PriceAmount}
                                              </td>
                                              <td>{b.PriceAmountPerUnit}</td>
                                              <td>{b.MarkupPercentage}</td>
                                              <td>{b.EstimatedRetailPrice}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </Table>
                                    </div>
                                  </Collapse>
                                </td>
                              </tr>
                            </React.Fragment>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="9" className="text-center text-muted py-4">
                            No pricing services found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </SettingsPanel>

      {/* CREATE / EDIT MODAL */}
      <AppModal
        open={isModalOpen}
        onClose={closeModal}
        title={isEditing ? "Edit Pricing Service" : "Add New Pricing Service"}
        headerAccent="indigo"
        size="lg"
        onSubmit={handleSubmit}
        submitLabel={isEditing ? "Save Changes" : "Create"}
        submitColor="primary"
      >
        <Row>
          <Col md={6}>
            <FormGroup className="mb-3">
              <Label htmlFor="Provider">Provider</Label>
              <Input
                id="Provider"
                name="Provider"
                value={formData.Provider}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup className="mb-3">
              <Label htmlFor="Category">Category</Label>
              <Input
                id="Category"
                name="Category"
                value={formData.Category}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup className="mb-3">
              <Label htmlFor="Name">Name</Label>
              <Input
                id="Name"
                name="Name"
                value={formData.Name}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup className="mb-3">
              <Label htmlFor="SkuCode">SKU Code</Label>
              <Input
                id="SkuCode"
                name="SkuCode"
                value={formData.SkuCode}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup className="mb-3">
              <Label htmlFor="CurrencyCode">Currency Code</Label>
              <Input
                id="CurrencyCode"
                name="CurrencyCode"
                value={formData.CurrencyCode}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup className="mb-3">
              <Label htmlFor="FlagUnlimitedFree">Unlimited Free Flag (e.g. 1)</Label>
              <Input
                id="FlagUnlimitedFree"
                name="FlagUnlimitedFree"
                value={formData.FlagUnlimitedFree}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup className="mb-3">
              <Label htmlFor="PriceEffectiveStartDateTime">
                Effective Start (YYYY-MM-DD HH:MM:SS)
              </Label>
              <Input
                id="PriceEffectiveStartDateTime"
                name="PriceEffectiveStartDateTime"
                value={formData.PriceEffectiveStartDateTime}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup className="mb-3">
              <Label htmlFor="PriceEffectiveEndDateTime">
                Effective End (blank = forever)
              </Label>
              <Input
                id="PriceEffectiveEndDateTime"
                name="PriceEffectiveEndDateTime"
                value={formData.PriceEffectiveEndDateTime}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup className="mb-3">
              <Label htmlFor="Url">URL</Label>
              <Input
                id="Url"
                name="Url"
                value={formData.Url}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup className="mb-3">
              <Label htmlFor="Notes">Notes</Label>
              <Input
                type="textarea"
                id="Notes"
                name="Notes"
                rows={2}
                value={formData.Notes}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>

        <hr />

        {/* BRACKETS EDITOR */}
        <div className="d-flex align-items-center justify-content-between mb-1">
          <h5 className="mb-0">Volume Brackets</h5>
          <Button color="soft-primary" size="sm" onClick={addBracket}>
            <i className="ri-add-line align-middle me-1"></i>
            Add Bracket
          </Button>
        </div>
        <p className="text-muted small mb-2">
          The free usage cap is simply the first bracket at Price Amount 0. Leave
          the upper bound blank for the top open-ended tier.
        </p>

        <div className="table-responsive">
          <Table className="table table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Vol. Lower</th>
                <th>Vol. Upper</th>
                <th>Per Qty</th>
                <th>Price Amt</th>
                <th>Markup %</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {brackets.map((bracket, index) => (
                <tr key={index}>
                  <td>
                    <Input
                      bsSize="sm"
                      name="VolumeLowerBound"
                      value={bracket.VolumeLowerBound}
                      onChange={(e) => handleBracketChange(index, e)}
                    />
                  </td>
                  <td>
                    <Input
                      bsSize="sm"
                      name="VolumeUpperBound"
                      value={bracket.VolumeUpperBound}
                      onChange={(e) => handleBracketChange(index, e)}
                    />
                  </td>
                  <td>
                    <Input
                      bsSize="sm"
                      name="PricePerUnitQuantity"
                      value={bracket.PricePerUnitQuantity}
                      onChange={(e) => handleBracketChange(index, e)}
                    />
                  </td>
                  <td>
                    <Input
                      bsSize="sm"
                      name="PriceAmount"
                      value={bracket.PriceAmount}
                      onChange={(e) => handleBracketChange(index, e)}
                    />
                  </td>
                  <td>
                    <Input
                      bsSize="sm"
                      name="MarkupPercentage"
                      value={bracket.MarkupPercentage}
                      onChange={(e) => handleBracketChange(index, e)}
                    />
                  </td>
                  <td className="text-end">
                    <Button
                      color="soft-danger"
                      size="sm"
                      onClick={() => removeBracket(index)}
                      disabled={brackets.length === 1}
                      title="Remove bracket"
                    >
                      <i className="ri-delete-bin-line align-middle"></i>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </AppModal>
    </React.Fragment>
  );
};

export default MasterThirdPartyPricing;
